import React from 'react'

const inputClass =
    'mt-2 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 shadow-sm outline-none focus:border-sky-500 focus:ring-2 focus:ring-sky-100 disabled:cursor-not-allowed disabled:bg-slate-100 disabled:text-slate-600'

const Field = ({ label, children, hint }) => (
    <label className="block text-sm font-medium text-slate-700">
        {label}
        {children}
        {hint && <span className="mt-1 block text-xs font-normal text-slate-500">{hint}</span>}
    </label>
)

const Section = ({ eyebrow, title, description, children }) => (
    <section className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
        <p className="text-xs font-semibold uppercase tracking-widest text-sky-700">{eyebrow}</p>
        <h2 className="mt-1 text-lg font-semibold text-slate-900">{title}</h2>
        {description && <p className="mt-1 text-sm text-slate-500">{description}</p>}
        <div className="mt-5 grid gap-5 md:grid-cols-2">{children}</div>
    </section>
)

export default function CapitalAllocationProposalForm({ value, onChange, disabled = false }) {
    const set = (field, next) => onChange({ ...value, [field]: next })
    const setNested = (field, key, next) => set(field, { ...value[field], [key]: next })
    const setEvidence = (index, key, next) => {
        const manifest = value.evidence_manifest.map((item, itemIndex) =>
            itemIndex === index ? { ...item, [key]: next } : item
        )
        set('evidence_manifest', manifest)
    }

    return (
        <div className="space-y-5">
            <Section eyebrow="Proposal information" title="Management proposal identity">
                <Field label="Proposal title">
                    <input
                        className={inputClass}
                        required
                        disabled={disabled}
                        value={value.title}
                        onChange={(event) => set('title', event.target.value)}
                    />
                </Field>
                <Field label="Proposal type">
                    <select
                        className={inputClass}
                        required
                        disabled={disabled}
                        value={value.proposal_type}
                        onChange={(event) => set('proposal_type', event.target.value)}
                    >
                        <option value="">Select a proposal type</option>
                        <option value="ANNUAL_PLANNING">Annual planning</option>
                        <option value="REALLOCATION">Reallocation</option>
                    </select>
                </Field>
                <Field label="Management owner">
                    <input
                        className={inputClass}
                        required
                        disabled={disabled}
                        value={value.management_owner}
                        onChange={(event) => set('management_owner', event.target.value)}
                    />
                </Field>
            </Section>

            <Section
                eyebrow="Executive summary"
                title="Executive Summary"
                description="Explain why the proposal exists, the decision requested, and the expected business outcome."
            >
                <div className="md:col-span-2">
                    <Field label="Executive summary">
                        <textarea
                            className={inputClass}
                            rows="5"
                            required
                            disabled={disabled}
                            value={value.executive_summary}
                            onChange={(event) => set('executive_summary', event.target.value)}
                        />
                    </Field>
                </div>
            </Section>

            <Section eyebrow="Strategic context" title="Strategic Context" description="Preserve the business objective separately from analytical evidence.">
                <div className="md:col-span-2"><Field label="Business objective"><textarea className={inputClass} rows="3" required disabled={disabled} value={value.business_objective} onChange={(event) => set('business_objective', event.target.value)} /></Field></div>
                <div className="md:col-span-2"><Field label="Strategic rationale and assumptions"><textarea className={inputClass} rows="4" required disabled={disabled} value={value.rationale} onChange={(event) => set('rationale', event.target.value)} /></Field></div>
            </Section>

            <Section eyebrow="Risk exposure" title="Risk Exposure" description="References identify the authoritative analysis; calculations are not copied into this package.">
                <Field label="Exposure description"><textarea className={inputClass} rows="3" required disabled={disabled} value={value.risk_exposure_reference.exposure_description} onChange={(event) => setNested('risk_exposure_reference', 'exposure_description', event.target.value)} /></Field>
                <Field label="Source reference"><input className={inputClass} required disabled={disabled} value={value.risk_exposure_reference.source_reference} onChange={(event) => setNested('risk_exposure_reference', 'source_reference', event.target.value)} /></Field>
                <Field label="Measurement date"><input className={inputClass} type="date" disabled={disabled} value={value.risk_exposure_reference.measurement_date} onChange={(event) => setNested('risk_exposure_reference', 'measurement_date', event.target.value)} /></Field>
                <Field label="Analysis reference"><input className={inputClass} disabled={disabled} value={value.risk_exposure_reference.analysis_reference} onChange={(event) => setNested('risk_exposure_reference', 'analysis_reference', event.target.value)} /></Field>
            </Section>

            <Section eyebrow="Management recommendation" title="Management Recommendation" description="Record Management's selected option and rationale, not the analytical recommendation payload.">
                <Field label="Management position"><select className={inputClass} disabled={disabled} value={value.management_position.position} onChange={(event) => setNested('management_position', 'position', event.target.value)}><option value="RECOMMENDATION">Support recommendation</option><option value="ALTERNATIVE">Select alternative</option></select></Field>
                <Field label="Selected option"><input className={inputClass} required disabled={disabled} value={value.management_position.selected_option} onChange={(event) => setNested('management_position', 'selected_option', event.target.value)} /></Field>
                <Field label="Alternative reference"><input className={inputClass} disabled={disabled} value={value.management_position.alternative_reference} onChange={(event) => setNested('management_position', 'alternative_reference', event.target.value)} /></Field>
                <div className="md:col-span-2"><Field label="Selection rationale"><textarea className={inputClass} rows="4" required disabled={disabled} value={value.selection_rationale} onChange={(event) => set('selection_rationale', event.target.value)} /></Field></div>
            </Section>

            <Section
                eyebrow="Capital allocation"
                title="Capital Allocation Intent"
                description="This is an allocation intent, not a calculated or optimized value."
            >
                <Field label="Total capital">
                    <input
                        className={inputClass}
                        type="number"
                        min="0"
                        step="any"
                        required
                        disabled={disabled}
                        value={value.requested_allocation.total_capital}
                        onChange={(event) => setNested('requested_allocation', 'total_capital', event.target.value)}
                    />
                </Field>
                <Field label="Currency">
                    <input
                        className={inputClass}
                        required
                        disabled={disabled}
                        placeholder="USD"
                        value={value.requested_allocation.currency}
                        onChange={(event) =>
                            setNested('requested_allocation', 'currency', event.target.value.toUpperCase())
                        }
                    />
                </Field>
                <Field label="Allocation type">
                    <input
                        className={inputClass}
                        required
                        disabled={disabled}
                        placeholder="Reallocation"
                        value={value.requested_allocation.allocation_type}
                        onChange={(event) => setNested('requested_allocation', 'allocation_type', event.target.value)}
                    />
                </Field>
                <Field label="Planning period"><input className={inputClass} disabled={disabled} value={value.requested_allocation.planning_period} onChange={(event) => setNested('requested_allocation', 'planning_period', event.target.value)} /></Field>
                <Field label="Allocation scope"><input className={inputClass} disabled={disabled} value={value.requested_allocation.allocation_scope} onChange={(event) => setNested('requested_allocation', 'allocation_scope', event.target.value)} /></Field>
            </Section>

            <Section
                eyebrow="Expected outcome"
                title="Expected Outcomes"
                description="Outcome references preserve lineage; they do not reproduce analytical results."
            >
                <div className="md:col-span-2">
                    <Field label="Resilience objectives" hint="Enter one objective per line.">
                        <textarea
                            className={inputClass}
                            rows="4"
                            required
                            disabled={disabled}
                            value={value.expected_outcomes.resilience_objectives.join('\n')}
                            onChange={(event) =>
                                setNested('expected_outcomes', 'resilience_objectives', event.target.value.split('\n'))
                            }
                        />
                    </Field>
                </div>
                <Field label="Risk reduction reference" hint="Reference only — no risk calculation is performed here.">
                    <input
                        className={inputClass}
                        disabled={disabled}
                        value={value.expected_outcomes.risk_reduction_reference}
                        onChange={(event) =>
                            setNested('expected_outcomes', 'risk_reduction_reference', event.target.value)
                        }
                    />
                </Field>
                <Field
                    label="Financial impact reference"
                    hint="Reference only — no financial calculation is performed here."
                >
                    <input
                        className={inputClass}
                        disabled={disabled}
                        value={value.expected_outcomes.financial_impact_reference}
                        onChange={(event) =>
                            setNested('expected_outcomes', 'financial_impact_reference', event.target.value)
                        }
                    />
                </Field>
                <Field label="Expected improvement"><input className={inputClass} required disabled={disabled} value={value.expected_outcomes.expected_improvement} onChange={(event) => setNested('expected_outcomes', 'expected_improvement', event.target.value)} /></Field>
                <Field label="Measurement horizon"><input className={inputClass} required disabled={disabled} value={value.expected_outcomes.measurement_horizon} onChange={(event) => setNested('expected_outcomes', 'measurement_horizon', event.target.value)} /></Field>
                <Field label="Accountable owner"><input className={inputClass} required disabled={disabled} value={value.expected_outcomes.accountable_owner} onChange={(event) => setNested('expected_outcomes', 'accountable_owner', event.target.value)} /></Field>
            </Section>

            <Section
                eyebrow="Supporting evidence"
                title="Evidence Manifest"
                description="Lineage references identify authoritative sources. Analytical values and results are never copied into this proposal."
            >
                <Field label="Decision Center reference" hint="Reference only">
                    <input
                        className={inputClass}
                        required
                        disabled={disabled}
                        value={value.decision_center_reference}
                        onChange={(event) => set('decision_center_reference', event.target.value)}
                    />
                </Field>
                <div className="space-y-4 md:col-span-2">
                    {value.evidence_manifest.map((item, index) => (
                        <div key={index} className="grid gap-3 rounded-lg border border-slate-200 bg-slate-50 p-4 md:grid-cols-2">
                            {['type', 'source_id', 'version', 'label', 'generated_at'].map((key) => (
                                <Field key={key} label={key.replaceAll('_', ' ')}>
                                    <input className={inputClass} required disabled={disabled}
                                        type={key === 'generated_at' ? 'datetime-local' : 'text'} value={item[key]}
                                        onChange={(event) => setEvidence(index, key, event.target.value)} />
                                </Field>
                            ))}
                            {!disabled && <button type="button" className="text-left text-sm font-semibold text-red-700" onClick={() => set('evidence_manifest', value.evidence_manifest.filter((_, itemIndex) => itemIndex !== index))}>Remove evidence reference</button>}
                        </div>
                    ))}
                    {!disabled && <button type="button" className="rounded-lg border border-sky-600 px-4 py-2 text-sm font-semibold text-sky-700" onClick={() => set('evidence_manifest', [...value.evidence_manifest, { type: '', source_id: '', version: '', label: '', generated_at: '' }])}>Add evidence reference</button>}
                </div>
            </Section>
        </div>
    )
}
